<script setup lang="ts">
const route = useRoute()
const jobs = useJobs()
const { defineOgMeta } = useSeoAttributes()
const {
  public: { baseUrl },
} = useRuntimeConfig()

const slug = route.params.slug as string

const { pending, data: item } = await useAsyncData(
  `jobs_${slug}`,
  async () => {
    return jobs.fetchBySlug(slug)
  },
  { watch: [route] },
)

if (!item.value?.job) {
  throw showError({ statusCode: 404 })
}

if (item.value?.job) {
  useHead(() => ({
    title: item.value?.job?.title || '',
    titleTemplate: '%s | Webamboos',
    meta: [...defineOgMeta(item.value?.meta || null)],
    link: [{ rel: 'canonical', href: `${baseUrl}/careers/${item.value?.job.slug}` }],
  }))
}
</script>

<template>
  <Loader :pending="pending">
    <Container v-if="item?.job" class="screen-content px-8 pb-32 md:px-16">
      <LayoutPageHeader />

      <div class="relative z-10 flex w-full flex-col gap-8 lg:flex-row">
        <div class="flex w-full flex-col gap-8 pt-0 md:pt-16 lg:w-1/2 lg:flex-row">
          <img
            :src="item.job.icon.url"
            class="bordershadow-main bg-main-50 z-1 h-16 w-16 rounded-lg p-3 xl:h-32 xl:w-32 xl:p-8"
            alt="Job Icon"
          />

          <div class="flex flex-col">
            <Heading as="h1" size="caption">
              {{ $t('careers.jobs.single.we_hiring') }}
            </Heading>

            <Heading as="h2" size="large" gradient="gray">
              {{ item.job.title }}
            </Heading>

            <div class="space-y-8 text-left">
              <div class="mt-6 flex flex-col gap-y-4">
                <div class="flex flex-row">
                  <img src="/jobs/job.svg" class="mr-3" alt="" />
                  <div class="flex flex-row text-base capitalize">
                    <p class="text-primary-gray w-32 font-bold">
                      {{ $t('careers.jobs.single.career_level') }}
                    </p>
                    <p class="text-primary-gray font-semibold">
                      {{ item.job.career_level }}
                    </p>
                  </div>
                </div>
                <div class="flex flex-row">
                  <img src="/jobs/location.svg" class="mr-3" alt="" />
                  <div class="flex flex-row text-base capitalize">
                    <p class="text-primary-gray w-32 font-bold">
                      {{ $t('careers.jobs.single.location') }}
                    </p>
                    <p class="text-primary-gray font-semibold">
                      {{ item.job.location }}
                    </p>
                  </div>
                </div>
                <div class="flex flex-row">
                  <img src="/jobs/position.svg" class="mr-3" alt="" />
                  <div class="flex flex-row text-base capitalize">
                    <p class="text-primary-gray w-32 font-bold">
                      {{ $t('careers.jobs.single.position_type') }}
                    </p>
                    <p class="text-primary-gray font-semibold">
                      {{ item.job.position_type }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="flex flex-col text-left lg:block">
                <Action as="link" :to="item.job.application_url" intent="main" size="lg">
                  {{ $t('careers.jobs.single.apply_now') }}
                </Action>
              </div>
            </div>
          </div>
        </div>

        <section id="requirements" class="w-full lg:w-1/2">
          <div class="bordershadow-gray rotate-1 rounded-lg bg-white p-4 md:p-8">
            <RichText :raw="item.job.job_description" />
          </div>

          <div class="bordershadow-gray -mt-4 -rotate-2 rounded-lg bg-white p-4 md:p-8">
            <Heading size="small">
              {{ $t('careers.jobs.single.requirements') }}
            </Heading>
            <RichText :raw="item.job.job_requirements" />
          </div>
        </section>
      </div>
    </Container>
  </Loader>
</template>
